import { useState } from 'react'
import { Search, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { LeadForm } from '@/components/LeadForm'
import { cn } from '@/lib/utils'
import { checkCepCoverage } from '@/services/cep'

export const CepChecker = () => {
  const [cep, setCep] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<'success' | 'error' | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [errorAnimation, setErrorAnimation] = useState(false)
  const [locationInfo, setLocationInfo] = useState<{
    city: string | null
    state: string | null
  } | null>(null)

  const formatCep = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{5})(\d)/, '$1-$2')
      .slice(0, 9)
  }

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCep(formatCep(e.target.value))
    setErrorAnimation(false)
  }

  const handleCheck = async () => {
    if (cep.length < 9) {
      setErrorAnimation(true)
      return
    }

    setIsLoading(true)
    setLocationInfo(null)

    try {
      const data = await checkCepCoverage(cep)

      setResult(data.covered ? 'success' : 'error')
      setLocationInfo({ city: data.city, state: data.state })
      setIsModalOpen(true)
    } catch (error) {
      console.error('Failed to check CEP', error)
      setResult('error')
      setIsModalOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCheck()
    }
  }

  const getLocationText = () => {
    if (!locationInfo?.city) return ''
    return `${locationInfo.city}${locationInfo.state ? ` - ${locationInfo.state}` : ''}`
  }

  const handleWhatsAppClick = () => {
    const message = `Olá, verifiquei meu CEP (${cep}) e vi que vocês atendem minha região${locationInfo?.city ? ` (${getLocationText()})` : ''}. Gostaria de um orçamento.`
    const whatsappUrl = `https://wa.me/+5527999205884?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  return (
    <section
      id="cep-checker"
      className="py-24 bg-primary relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg
          className="h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10 text-center">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-12 animate-fade-in-up">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            Atendemos na sua cidade?
          </h2>
          <p className="text-gray-600 mb-8">
            Para verificarmos, a única informação necessária é o seu CEP
          </p>

          <div
            className={cn(
              'flex flex-col sm:flex-row gap-4',
              errorAnimation && 'animate-shake',
            )}
          >
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="00000-000"
                value={cep}
                onChange={handleCepChange}
                onKeyDown={handleKeyDown}
                className="h-14 text-lg px-4 border-2 focus-visible:ring-primary focus-visible:border-primary"
                maxLength={9}
              />
            </div>
            <Button
              onClick={handleCheck}
              disabled={isLoading || cep.length < 9}
              size="lg"
              className="h-14 px-8 text-lg font-semibold bg-primary hover:bg-primary/90"
            >
              {isLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Verificar
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Result Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] p-0 flex flex-col gap-0 border-none outline-none overflow-hidden">
          <DialogHeader className="p-6 pb-2 shrink-0">
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors duration-500 animate-grow-in"
              style={{
                backgroundColor: result === 'success' ? '#DCFCE7' : '#FEE2E2',
              }}
            >
              {result === 'success' ? (
                <CheckCircle className="w-10 h-10 text-green-600" />
              ) : (
                <XCircle className="w-10 h-10 text-red-600" />
              )}
            </div>
            <DialogTitle className="text-2xl font-bold text-center">
              {result === 'success'
                ? 'Ótima notícia!'
                : 'Ainda não chegamos aí'}
            </DialogTitle>
            <DialogDescription className="text-center text-base mt-2">
              {result === 'success' ? (
                <>
                  Atendemos{' '}
                  {locationInfo?.city
                    ? `em ${getLocationText()}`
                    : 'sua região'}{' '}
                  com toda a nossa frota e serviços especializados.
                </>
              ) : (
                <>
                  {locationInfo?.city
                    ? `Ainda não estamos operando em ${getLocationText()}.`
                    : 'Não encontramos cobertura para este CEP no momento.'}
                  <br />
                  Deixe seus dados para ser avisado sobre futuras expansões ou
                  condições especiais.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 pt-2">
            {result === 'success' ? (
              <div className="space-y-4 pt-4">
                <Button
                  className="w-full h-12 text-lg bg-[#25D366] hover:bg-[#20bd5a] text-white"
                  onClick={handleWhatsAppClick}
                >
                  Falar com Consultor via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsModalOpen(false)}
                >
                  Pesquisar outro CEP
                </Button>
              </div>
            ) : (
              <LeadForm
                defaultCity={locationInfo?.city || ''}
                onSuccess={() => setTimeout(() => setIsModalOpen(false), 2000)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
